/**
 * Pure, frontend-only heuristic for the volume of a per-horizon export.
 *
 * Conservative defaults — refined when better numbers exist:
 *  - 15-minute resolution => 96 records/day
 *  - 9 quantiles (q05..q95)
 *  - intraday (horizon 0) assumes hourly gate closures (~24/day) as worst case
 *  - day-ahead (horizon >= 1) assumes a single gate closure per day
 */
const BYTES_PER_ROW = 50;
const DEFAULT_RESOLUTION_MIN = 15;
const DEFAULT_QUANTILES = 9;

function gcPerDay(horizon) {
  if (horizon === 0) return 24; // intraday worst-case hourly GCs
  return 1; // day-ahead — one GC/day
}

function modeFor(rows) {
  if (rows < 100_000) return 'instant';
  if (rows < 1_000_000) return 'async-fast';
  return 'async-slow';
}

export function estimateExport({
  horizon,
  startDate,
  endDate,
  resolutionMinutes = DEFAULT_RESOLUTION_MIN,
  quantiles = DEFAULT_QUANTILES,
}) {
  if (horizon === null || horizon === undefined) return null;
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const days = Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
  if (!Number.isFinite(days) || days <= 0) return null;

  const recordsPerDay = (24 * 60) / resolutionMinutes;
  const rows = Math.round(days * recordsPerDay * gcPerDay(horizon) * quantiles);
  const sizeBytes = rows * BYTES_PER_ROW;

  return { rows, sizeBytes, mode: modeFor(rows) };
}

export function formatEstimateHint(estimate) {
  if (!estimate) return '';
  const { rows, sizeBytes, mode } = estimate;
  const rowsLabel =
    rows >= 1_000_000 ? `~${(rows / 1_000_000).toFixed(1)}M rows`
    : rows >= 1_000 ? `~${Math.round(rows / 1_000)}k rows`
    : `~${rows} rows`;
  const sizeLabel =
    sizeBytes >= 1_000_000 ? `~${Math.round(sizeBytes / 1_000_000)} MB`
    : sizeBytes >= 1_000 ? `~${Math.round(sizeBytes / 1_000)} KB`
    : `<1 KB`;
  const modeLabel =
    mode === 'instant' ? 'instant'
    : mode === 'async-fast' ? 'ready in minutes'
    : 'up to 24h';
  return `${rowsLabel} · ${sizeLabel} · ${modeLabel}`;
}
